import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PhoneOutlinedIcon from "@material-ui/icons/PhoneOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import AddIcon from "@material-ui/icons/Add";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import ContactModal from "../ContactModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import TicketTagsModal from "../TicketTagsModal";
import MarkdownWrapper from "../MarkdownWrapper";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactAvatar from "../ContactAvatar";

const drawerWidth = 320;

const useStyles = makeStyles(theme => {
  const isDark = theme.palette.type === "dark";
  const ink2 = isDark ? "#cbd5e1" : "#334155";
  const ink3 = isDark ? "#94a3b8" : "#64748b";
  const ink4 = isDark ? "#64748b" : "#94a3b8";
  const line2 = isDark ? "rgba(255,255,255,0.12)" : "#dfe5ec";
  const sectionBorder = isDark ? "rgba(255,255,255,0.06)" : "#aacfc4";
  const cardBg = isDark ? "rgba(255,255,255,0.05)" : "#ffffff";

  return {
    drawer: {
      width: drawerWidth,
      flexShrink: 0
    },
    drawerPaper: {
      width: drawerWidth,
      display: "flex",
      border: "none",
      borderLeft: `1px solid ${sectionBorder}`
    },
    header: {
      display: "flex",
      borderBottom: `1px solid ${sectionBorder}`,
      backgroundColor: isDark ? "#0f231f" : "#d3e8e0",
      alignItems: "center",
      padding: theme.spacing(0, 1),
      minHeight: "73px",
      justifyContent: "flex-start"
    },
    content: {
      display: "flex",
      flexDirection: "column",
      background: isDark ? "#0b1c18" : "#cadfd7",
      height: "100%",
      overflowY: "auto",
      ...theme.scrollbarStyles
    },

    hero: {
      padding: "15px 20px 13px",
      textAlign: "center",
      background: isDark
        ? "linear-gradient(180deg, #0f231f 0%, #0b1c18 100%)"
        : "linear-gradient(180deg, #d9e9e3 0%, #cadfd7 100%)",
      borderBottom: `1px solid ${sectionBorder}`
    },
    heroAvatarWrap: {
      position: "relative",
      display: "inline-block"
    },
    heroAvatar: {
      width: 56,
      height: 56,
      margin: "0 auto",
      fontSize: 20,
      fontWeight: 700,
      color: "#fff",
      background: "linear-gradient(135deg, #36a08e 0%, #1f6f5c 100%)",
      border: "2px solid #fff",
      boxShadow: "0 8px 18px rgba(15, 23, 42, 0.14)"
    },
    heroStatus: {
      position: "absolute",
      right: 1,
      bottom: 1,
      width: 14,
      height: 14,
      borderRadius: "50%",
      backgroundColor: "#22c55e",
      border: "3px solid #fff"
    },
    heroName: {
      fontSize: "1.06rem",
      fontWeight: 700,
      marginTop: 8,
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary
    },
    heroMeta: {
      fontSize: "0.78rem",
      color: ink3,
      marginTop: 2
    },

    section: {
      padding: "16px 20px",
      borderBottom: `1px solid ${sectionBorder}`
    },
    sectionHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 12
    },
    sectionTitle: {
      fontSize: "0.69rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      color: ink3
    },
    sectionLink: {
      fontSize: "0.75rem",
      fontWeight: 600,
      color: theme.palette.primary.main,
      cursor: "pointer",
      "&:hover": { textDecoration: "underline" }
    },

    fieldRow: {
      display: "flex",
      alignItems: "center",
      gap: 11,
      padding: "7px 0"
    },
    fieldIcon: {
      width: 32,
      height: 32,
      borderRadius: 9,
      backgroundColor: cardBg,
      border: `1px solid ${line2}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: ink3,
      flexShrink: 0,
      "& svg": { fontSize: 17 }
    },
    fieldKey: {
      color: ink4,
      fontSize: "0.69rem",
      fontWeight: 600,
      lineHeight: 1.2
    },
    fieldValue: {
      color: ink2,
      fontWeight: 600,
      fontSize: "0.81rem",
      wordBreak: "break-word"
    },

    tagWrap: {
      display: "flex",
      flexWrap: "wrap",
      gap: 7
    },
    ctag: {
      fontSize: "0.72rem",
      fontWeight: 600,
      padding: "5px 11px",
      borderRadius: 99,
      display: "inline-flex",
      alignItems: "center",
      gap: 6
    },
    ctagDot: {
      width: 7,
      height: 7,
      borderRadius: "50%",
      flexShrink: 0
    },
    ctagAdd: {
      border: `1px dashed ${line2}`,
      color: ink3,
      background: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      "& svg": { fontSize: 14 },
      "&:hover": {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main
      }
    },

    opActions: {
      display: "flex",
      flexDirection: "column",
      gap: 8
    },
    opBtn: {
      display: "flex",
      alignItems: "center",
      gap: 9,
      width: "100%",
      padding: "9px 12px",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.14)" : "#9ec6ba"}`,
      backgroundColor: cardBg,
      borderRadius: 10,
      fontFamily: "inherit",
      fontSize: "0.81rem",
      fontWeight: 600,
      color: theme.palette.primary.main,
      cursor: "pointer",
      textAlign: "left",
      textTransform: "none",
      justifyContent: "flex-start",
      "&:hover": {
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "#f1faf6",
        borderColor: theme.palette.primary.main
      },
      "& svg": { fontSize: 16, flexShrink: 0 }
    },
    opBtnDoc: {
      marginLeft: "auto",
      fontSize: "0.6rem",
      fontWeight: 700,
      color: "#9a6700",
      backgroundColor: "#fcecc9",
      padding: "2px 7px",
      borderRadius: 99,
      flexShrink: 0
    },
    operationResult: {
      marginTop: 10,
      padding: 8,
      borderRadius: 8,
      backgroundColor: isDark ? "rgba(2, 6, 23, 0.45)" : "rgba(255,255,255,0.85)",
      border: `1px solid ${line2}`
    },
    operationResultCode: {
      fontFamily: "Consolas, 'Courier New', monospace",
      fontSize: "0.78rem",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      color: theme.palette.text.secondary
    },

    histSearch: {
      marginBottom: 10
    },
    histItem: {
      display: "flex",
      gap: 11,
      padding: "11px 0",
      cursor: "pointer",
      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "#b9d8cf"}`,
      "&:last-child": { borderBottom: "none", paddingBottom: 2 }
    },
    histItemActive: {
      "& $histNum": {
        boxShadow: `0 0 0 1.5px ${theme.palette.primary.main}`
      }
    },
    histIcon: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      backgroundColor: isDark ? "rgba(124,92,255,0.18)" : "#eef0fb",
      color: "#5849c4",
      "& svg": { fontSize: 17 }
    },
    histIconDone: {
      backgroundColor: isDark ? "rgba(34,197,94,0.16)" : "#e7f6ee",
      color: "#1a7a4d"
    },
    histBody: {
      flex: 1,
      minWidth: 0
    },
    histRow1: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 6,
      flexWrap: "wrap"
    },
    histMeta: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.72rem",
      color: ink3,
      marginTop: 3,
      "& svg": { fontSize: 13, color: ink4, flexShrink: 0 }
    },
    histMetaStrong: {
      color: ink2,
      fontWeight: 600
    },
    histNum: {
      fontSize: "0.66rem",
      fontWeight: 700,
      padding: "3px 9px",
      borderRadius: 99,
      backgroundColor: "#fff4e0",
      color: "#9a6700"
    },
    histStatus: {
      fontSize: "0.66rem",
      fontWeight: 700,
      padding: "3px 9px",
      borderRadius: 99
    },
    histStatusOk: {
      backgroundColor: isDark ? "rgba(34,197,94,0.16)" : "#e7f6ee",
      color: "#1a7a4d"
    },
    histStatusOpen: {
      backgroundColor: isDark ? "rgba(124,92,255,0.18)" : "#eef0fb",
      color: "#5849c4"
    },
    emptyText: {
      fontSize: "0.8rem",
      color: ink3
    }
  };
});

const formatTicketNumber = ticketId =>
  `#${String(ticketId || "").padStart(6, "0")}`;

const getInitials = name =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .map(part => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const formatPhoneDisplay = raw => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return "-";
  if (digits.length >= 12 && digits.startsWith("55")) {
    const ddd = digits.slice(2, 4);
    const rest = digits.slice(4);
    return `+55 (${ddd}) ${rest.slice(0, rest.length - 4)}-${rest.slice(-4)}`;
  }
  return `+${digits}`;
};

const formatTicketStatus = status => {
  switch (status) {
    case "open":
      return "Atendendo";
    case "pending":
      return "Aguardando";
    case "closed":
      return "Resolvido";
    default:
      return status;
  }
};

const formatDocument = value => {
  const digits = (value || "").replace(/\D/g, "");

  if (!digits) return "";

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const formatDocumentDisplay = value => formatDocument(value) || "-";

const normalizeDigits = value => String(value || "").replace(/\D/g, "");
const normalizeContact = value =>
  value && typeof value === "object" ? value : {};

const normalizePersonType = value => {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "PF" || normalized === "PJ") {
    return normalized;
  }
  return "";
};

const isRepeatedDigits = value => /^(\d)\1+$/.test(value);

const isValidCpf = value => {
  const cpf = normalizeDigits(value);
  if (cpf.length !== 11 || isRepeatedDigits(cpf)) {
    return false;
  }

  let sum = 0;
  for (let index = 0; index < 9; index += 1) {
    sum += Number(cpf[index]) * (10 - index);
  }

  let digit = (sum * 10) % 11;
  if (digit === 10) {
    digit = 0;
  }

  if (digit !== Number(cpf[9])) {
    return false;
  }

  sum = 0;
  for (let index = 0; index < 10; index += 1) {
    sum += Number(cpf[index]) * (11 - index);
  }

  digit = (sum * 10) % 11;
  if (digit === 10) {
    digit = 0;
  }

  return digit === Number(cpf[10]);
};

const isValidCnpj = value => {
  const cnpj = normalizeDigits(value);
  if (cnpj.length !== 14 || isRepeatedDigits(cnpj)) {
    return false;
  }

  const calcDigit = (base, factors) => {
    const total = base
      .split("")
      .reduce((sum, item, index) => sum + Number(item) * factors[index], 0);
    const remainder = total % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  const digit1 = calcDigit(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  if (digit1 !== Number(cnpj[12])) {
    return false;
  }

  const digit2 = calcDigit(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return digit2 === Number(cnpj[13]);
};

const validateDocumentAndType = (documentValue, personTypeValue) => {
  const document = normalizeDigits(documentValue);
  const personType = normalizePersonType(personTypeValue);

  if (!document) {
    return {
      valid: false,
      message: "Informe CPF/CNPJ para executar esta ação."
    };
  }

  const inferredPersonType = document.length === 11 ? "PF" : document.length === 14 ? "PJ" : "";
  if (!inferredPersonType) {
    return {
      valid: false,
      message: "CPF/CNPJ inválido. Informe 11 (CPF) ou 14 (CNPJ) digitos."
    };
  }

  const effectivePersonType = personType || inferredPersonType;
  if (effectivePersonType === "PF" && !isValidCpf(document)) {
    return {
      valid: false,
      message: "CPF inválido. Revise os digitos informados."
    };
  }

  if (effectivePersonType === "PJ" && !isValidCnpj(document)) {
    return {
      valid: false,
      message: "CNPJ inválido. Revise os digitos informados."
    };
  }

  if (personType && personType !== inferredPersonType) {
    return {
      valid: false,
      message: "Tipo de cadastro não confere com o documento informado."
    };
  }

  return {
    valid: true,
    document,
    personType: effectivePersonType
  };
};

const normalizeResultMode = value => {
  const normalized = String(value || "display")
    .trim()
    .toLowerCase();

  if (["display", "send_conversation", "open_redirect"].includes(normalized)) {
    return normalized;
  }

  return "display";
};

const isHttpUrl = value => {
  try {
    const parsed = new URL(String(value || ""));
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getFirstHttpUrlFromText = value => {
  const text = String(value || "");
  const match = text.match(/https?:\/\/[^\s"']+/i);
  return match ? match[0] : "";
};

const findFirstUrlInJson = value => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return isHttpUrl(value) ? value : "";
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findFirstUrlInJson(item);
      if (found) {
        return found;
      }
    }
    return "";
  }

  if (typeof value === "object") {
    const preferredKeys = [
      "url",
      "link",
      "redirectUrl",
      "redirect_url",
      "downloadUrl",
      "download_url",
      "boletoUrl",
      "boleto_url",
      "paymentUrl",
      "payment_url"
    ];

    for (const key of preferredKeys) {
      if (typeof value[key] === "string" && isHttpUrl(value[key])) {
        return value[key];
      }
    }

    for (const nestedKey of Object.keys(value)) {
      const found = findFirstUrlInJson(value[nestedKey]);
      if (found) {
        return found;
      }
    }
  }

  return "";
};

const getActionResultText = (action, executionData) => {
  const templateOutput = String(executionData?.resultOutput || "").trim();
  if (templateOutput) {
    return templateOutput;
  }

  const bodyPreview = String(executionData?.response?.bodyPreview || "").trim();
  if (bodyPreview) {
    return bodyPreview;
  }

  const finalUrl = String(executionData?.response?.finalUrl || "").trim();
  if (finalUrl) {
    return finalUrl;
  }

  return `A ação "${String(action?.name || "API Tools")}" foi executada com sucesso.`;
};

const getActionResultUrl = (action, executionData) => {
  const templateOutput = String(executionData?.resultOutput || "").trim();
  if (templateOutput) {
    if (isHttpUrl(templateOutput)) {
      return templateOutput;
    }

    const fromTemplate = getFirstHttpUrlFromText(templateOutput);
    if (fromTemplate) {
      return fromTemplate;
    }
  }

  const bodyPreview = String(executionData?.response?.bodyPreview || "").trim();
  if (bodyPreview) {
    try {
      const parsed = JSON.parse(bodyPreview);
      const fromJson = findFirstUrlInJson(parsed);
      if (fromJson) {
        return fromJson;
      }
    } catch (error) {
      const fromText = getFirstHttpUrlFromText(bodyPreview);
      if (fromText) {
        return fromText;
      }
    }
  }

  const finalUrl = String(executionData?.response?.finalUrl || "").trim();
  if (isHttpUrl(finalUrl)) {
    return finalUrl;
  }

  const endpoint = String(action?.endpoint || "").trim();
  if (isHttpUrl(endpoint) && !endpoint.includes("{{")) {
    return endpoint;
  }

  return "";
};

const getActionModeLabel = action => {
  const mode = normalizeResultMode(action?.resultMode);
  if (mode === "send_conversation") {
    return " - Envia na conversa";
  }
  if (mode === "open_redirect") {
    return " - Abre link";
  }
  return "";
};

const closeWindowSafely = openedWindow => {
  if (!openedWindow) {
    return;
  }

  try {
    if (!openedWindow.closed) {
      openedWindow.close();
    }
  } catch (error) {
    // noop
  }
};

const openRedirectPlaceholderTab = action => {
  if (normalizeResultMode(action?.resultMode) !== "open_redirect") {
    return null;
  }

  const openedWindow = window.open("about:blank", "_blank");
  if (!openedWindow) {
    toast.warn("Não foi possível abrir nova aba. Habilite pop-ups para este site.");
    return null;
  }

  try {
    openedWindow.opener = null;
    openedWindow.document.title = "Carregando...";
  } catch (error) {
    // noop
  }

  return openedWindow;
};

const ContactDrawer = ({
  open,
  handleDrawerClose,
  contact,
  ticket,
  loading,
  currentTicketId,
  ticketHistory = [],
  onRefreshTicket
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [modalOpen, setModalOpen] = useState(false);
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [localContact, setLocalContact] = useState(normalizeContact(contact));
  const [activeInfoTab, setActiveInfoTab] = useState("data");
  const [operationActions, setOperationActions] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [executingActionId, setExecutingActionId] = useState(null);
  const [operationResult, setOperationResult] = useState(null);
  const [historyFilter, setHistoryFilter] = useState("");
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [documentSaving, setDocumentSaving] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [pendingAction, setPendingAction] = useState(null);
  const [documentForm, setDocumentForm] = useState({
    personType: "",
    document: ""
  });

  const safeContact = useMemo(() => normalizeContact(localContact), [localContact]);

  const orderedHistory = useMemo(() => {
    return Array.isArray(ticketHistory) ? ticketHistory : [];
  }, [ticketHistory]);

  const filteredHistory = useMemo(() => {
    const query = String(historyFilter || "").trim().toLowerCase();
    if (!query) return orderedHistory;

    return orderedHistory.filter(item => {
      const ticketNumber = formatTicketNumber(item.id).toLowerCase();
      const subject = String(item.subject || "").toLowerCase();
      const resolutionSummary = String(item.resolutionSummary || "").toLowerCase();
      const message = String(item.lastMessage || "").toLowerCase();
      const userName = String(item.user?.name || "").toLowerCase();
      const queueName = String(item.queue?.name || "").toLowerCase();
      const status = String(formatTicketStatus(item.status) || "").toLowerCase();

      return (
        ticketNumber.includes(query) ||
        subject.includes(query) ||
        resolutionSummary.includes(query) ||
        message.includes(query) ||
        userName.includes(query) ||
        queueName.includes(query) ||
        status.includes(query)
      );
    });
  }, [historyFilter, orderedHistory]);

  useEffect(() => {
    const normalized = normalizeContact(contact);
    setLocalContact(normalized);
    setActiveInfoTab("data");
    setHistoryFilter("");
    setDocumentDialogOpen(false);
    setDocumentError("");
    setPendingAction(null);
    setDocumentForm({
      personType: normalizePersonType(normalized?.personType),
      document: formatDocument(normalized?.document || "")
    });
  }, [contact]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadActions = async () => {
      setLoadingOperations(true);
      try {
        const { data } = await api.get("/api-tools/actions/available");
        setOperationActions(Array.isArray(data) ? data : []);
      } catch (error) {
        setOperationActions([]);
        toastError(error);
      } finally {
        setLoadingOperations(false);
      }
    };

    loadActions();
  }, [open]);

  const executeActionRequest = async (action, contactId, redirectWindow = null) => {
    setExecutingActionId(action.id);
    setOperationResult(null);
    try {
      const { data } = await api.post(`/api-tools/actions/${action.id}/execute`, {
        ticketId: Number(currentTicketId) || undefined,
        contactId: Number(contactId) || Number(safeContact?.id) || undefined
      });

      const resultMode = normalizeResultMode(action?.resultMode);
      const requestSucceeded = Boolean(data?.success);
      const resultText = getActionResultText(action, data);
      setOperationResult({
        actionName: action.name,
        success: requestSucceeded,
        statusCode: data?.response?.statusCode,
        bodyPreview: resultText || data?.response?.bodyPreview || ""
      });

      if (!requestSucceeded) {
        if (resultMode === "open_redirect") {
          closeWindowSafely(redirectWindow);
        }
        return;
      }

      if (resultMode === "send_conversation") {
        const ticketId = Number(currentTicketId);
        if (!ticketId) {
          toast.warn("Não foi possível enviar o resultado: ticket não identificado.");
          return;
        }

        const messageBody = getActionResultText(action, data);
        if (!String(messageBody || "").trim()) {
          toast.warn("A ação foi executada, mas não retornou conteudo para enviar.");
          return;
        }

        await api.post(`/messages/${ticketId}`, {
          body: messageBody
        });
        toast.success("Resultado enviado na conversa.");
        return;
      }

      if (resultMode === "open_redirect") {
        const targetUrl = getActionResultUrl(action, data);
        if (!targetUrl) {
          closeWindowSafely(redirectWindow);
          toast.warn("A ação foi executada, mas nenhuma URL válida foi encontrada.");
          return;
        }

        if (redirectWindow && !redirectWindow.closed) {
          try {
            redirectWindow.location.replace(targetUrl);
          } catch (error) {
            try {
              redirectWindow.location.href = targetUrl;
            } catch (nestedError) {
              closeWindowSafely(redirectWindow);
              toast.warn("Não foi possível redirecionar a nova aba.");
              return;
            }
          }
          toast.success("Abrindo resultado da ação.");
          return;
        }

        const openedWindow = window.open(targetUrl, "_blank");
        if (!openedWindow) {
          toast.warn("Não foi possível abrir nova aba. Habilite pop-ups para este site.");
          return;
        }

        try {
          openedWindow.opener = null;
        } catch (error) {
          // noop
        }
        toast.success("Abrindo resultado da ação.");
      }
    } catch (error) {
      const backendErrorCode = String(
        error?.response?.data?.error || error?.response?.data?.message || ""
      ).trim();

      if (
        backendErrorCode === "ERR_API_TOOL_DOCUMENT_REQUIRED" ||
        backendErrorCode === "ERR_API_TOOL_DOCUMENT_INVALID"
      ) {
        openDocumentDialog(action);
        return;
      }

      if (normalizeResultMode(action?.resultMode) === "open_redirect") {
        closeWindowSafely(redirectWindow);
      }
      setOperationResult({
        actionName: action.name,
        success: false,
        statusCode: error?.response?.status || 0,
        bodyPreview:
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao executar a ação."
      });
      toastError(error);
    } finally {
      setExecutingActionId(null);
    }
  };

  const openDocumentDialog = action => {
    setPendingAction(action);
    setDocumentError("");
    setDocumentForm({
      personType: normalizePersonType(safeContact?.personType),
      document: formatDocument(safeContact?.document || "")
    });
    setDocumentDialogOpen(true);
  };

  const handleExecuteAction = async action => {
    if (Boolean(action?.requiresDocument)) {
      if (Boolean(localContact?.isGroup)) {
        setOperationResult({
          actionName: action.name,
          success: false,
          statusCode: 400,
          bodyPreview: "Esta ação exige CPF/CNPJ e não pode ser usada em contatos de grupo."
        });
        return;
      }

      const validation = validateDocumentAndType(
        safeContact?.document,
        safeContact?.personType
      );

      if (!validation.valid) {
        openDocumentDialog(action);
        return;
      }
    }

    const redirectWindow = openRedirectPlaceholderTab(action);
    await executeActionRequest(
      action,
      Number(safeContact?.id) || undefined,
      redirectWindow
    );
  };

  const handleSaveDocumentAndExecute = async () => {
    if (!pendingAction) {
      setDocumentDialogOpen(false);
      return;
    }

    const validation = validateDocumentAndType(
      documentForm.document,
      documentForm.personType
    );

    if (!validation.valid) {
      setDocumentError(validation.message);
      return;
    }

    if (!safeContact?.id) {
      setDocumentError("Contato inválido para atualizar CPF/CNPJ.");
      return;
    }

    setDocumentSaving(true);
    setDocumentError("");
    try {
      const { data } = await api.put(`/contacts/${safeContact.id}`, {
        personType: validation.personType,
        document: validation.document
      });

      setLocalContact(normalizeContact(data));
      setDocumentDialogOpen(false);
      const actionToExecute = pendingAction;
      setPendingAction(null);
      const redirectWindow = openRedirectPlaceholderTab(actionToExecute);
      await executeActionRequest(
        actionToExecute,
        Number(data?.id) || Number(safeContact.id),
        redirectWindow
      );
    } catch (error) {
      toastError(error);
      setDocumentError(
        error?.response?.data?.message || "Falha ao salvar CPF/CNPJ do contato."
      );
    } finally {
      setDocumentSaving(false);
    }
  };

  const handleOpenHistoryTicket = ticketId => {
    if (!ticketId) return;
    history.push(`/tickets/${ticketId}`);
    handleDrawerClose();
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
      ModalProps={{
        container: document.getElementById("drawer-container"),
        style: { position: "absolute" }
      }}
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <div className={classes.header}>
        <IconButton onClick={handleDrawerClose}>
          <CloseIcon />
        </IconButton>
        <Typography style={{ justifySelf: "center" }}>
          {i18n.t("contactDrawer.header", {
            defaultValue: "Dados do contato"
          })}
        </Typography>
      </div>

      {loading ? (
        <ContactDrawerSkeleton classes={classes} />
      ) : (
        <div className={classes.content}>
          <ContactModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            contactId={safeContact.id}
            onSave={updatedContact => setLocalContact(normalizeContact(updatedContact))}
          />
          <TicketTagsModal
            open={tagsModalOpen}
            onClose={() => setTagsModalOpen(false)}
            ticket={ticket}
            onSaved={() => {
              if (typeof onRefreshTicket === "function") onRefreshTicket();
            }}
          />

          <div className={classes.hero}>
            <span className={classes.heroAvatarWrap}>
              <ContactAvatar
                alt={safeContact.name}
                contact={safeContact}
                className={classes.heroAvatar}
              >
                {getInitials(safeContact.name)}
              </ContactAvatar>
              <span className={classes.heroStatus} />
            </span>
            <div className={classes.heroName}>{safeContact.name || "Contato"}</div>
            {safeContact.isGroup ? (
              <div className={classes.heroMeta}>Grupo do WhatsApp</div>
            ) : null}
          </div>

          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.sectionTitle}>INFORMAÇÕES</span>
              <span
                className={classes.sectionLink}
                onClick={() => setModalOpen(true)}
              >
                Editar
              </span>
            </div>

            <div className={classes.fieldRow}>
              <div className={classes.fieldIcon}>
                <PhoneOutlinedIcon />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className={classes.fieldKey}>Telefone</div>
                <div className={classes.fieldValue}>
                  {safeContact.isGroup ? "-" : formatPhoneDisplay(safeContact.number)}
                </div>
              </div>
            </div>

            <div className={classes.fieldRow}>
              <div className={classes.fieldIcon}>
                <MailOutlineIcon />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className={classes.fieldKey}>E-mail</div>
                <div className={classes.fieldValue}>{safeContact.email || "-"}</div>
              </div>
            </div>

            <div className={classes.fieldRow}>
              <div className={classes.fieldIcon}>
                <AssignmentIndOutlinedIcon />
              </div>
              <div style={{ minWidth: 0 }}>
                <div className={classes.fieldKey}>CPF/CNPJ</div>
                <div className={classes.fieldValue}>
                  {formatDocumentDisplay(safeContact.document)}
                </div>
              </div>
            </div>

            {(Array.isArray(safeContact.extraInfo) ? safeContact.extraInfo : []).map(
              info => (
                <div key={info.id} className={classes.fieldRow}>
                  <div className={classes.fieldIcon}>
                    <AssignmentIndOutlinedIcon />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className={classes.fieldKey}>{info.name}</div>
                    <div className={classes.fieldValue}>
                      <MarkdownWrapper>{info.value}</MarkdownWrapper>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>

          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.sectionTitle}>ETIQUETAS</span>
            </div>
            <div className={classes.tagWrap}>
              {(Array.isArray(ticket?.tags) ? ticket.tags : []).map(tag => (
                <span
                  key={tag.id}
                  className={classes.ctag}
                  style={{
                    backgroundColor: `${tag.color}22`,
                    color: tag.color
                  }}
                >
                  <span
                    className={classes.ctagDot}
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </span>
              ))}
              <span
                className={`${classes.ctag} ${classes.ctagAdd}`}
                onClick={() => setTagsModalOpen(true)}
              >
                <AddIcon />
                Adicionar
              </span>
            </div>
          </div>

          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <span className={classes.sectionTitle}>AÇÕES</span>
            </div>
            {loadingOperations ? (
              <Box display="flex" alignItems="center" gridGap={8}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="textSecondary">
                  Carregando ações...
                </Typography>
              </Box>
            ) : operationActions.length === 0 ? (
              <Typography className={classes.emptyText}>
                Sem ações disponíveis para o seu usuário.
              </Typography>
            ) : (
              <div className={classes.opActions}>
                {operationActions.map(action => {
                  const running =
                    Number(executingActionId) === Number(action.id);
                  return (
                    <button
                      key={action.id}
                      type="button"
                      className={classes.opBtn}
                      onClick={() => handleExecuteAction(action)}
                      disabled={running}
                    >
                      <PlayArrowIcon />
                      {running
                        ? "Executando..."
                        : `${action.name}${getActionModeLabel(action)}`}
                      {action.requiresDocument ? (
                        <span className={classes.opBtnDoc}>CPF/CNPJ</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
            {operationResult ? (
              <div className={classes.operationResult}>
                <Typography variant="body2">
                  {operationResult.actionName} -{" "}
                  {operationResult.success ? "Sucesso" : "Falha"} (
                  {operationResult.statusCode || "-"})
                </Typography>
                {operationResult.bodyPreview ? (
                  <Typography className={classes.operationResultCode}>
                    {String(operationResult.bodyPreview)}
                  </Typography>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className={classes.section} style={{ borderBottom: "none" }}>
            <div className={classes.sectionHead}>
              <span className={classes.sectionTitle}>HISTÓRICO</span>
            </div>

            {orderedHistory.length === 0 ? (
              <Typography className={classes.emptyText}>
                Nenhum outro ticket encontrado para este contato.
              </Typography>
            ) : (
              filteredHistory.map(item => {
                const isCurrent = Number(item.id) === Number(currentTicketId);
                const isClosed = item.status === "closed";
                return (
                  <div
                    key={item.id}
                    className={`${classes.histItem} ${
                      isCurrent ? classes.histItemActive : ""
                    }`}
                    onClick={() => handleOpenHistoryTicket(item.id)}
                  >
                    <div
                      className={`${classes.histIcon} ${
                        isClosed ? classes.histIconDone : ""
                      }`}
                    >
                      {isClosed ? <DoneAllIcon /> : <ScheduleIcon />}
                    </div>
                    <div className={classes.histBody}>
                      <div className={classes.histRow1}>
                        <span className={classes.histNum}>
                          {formatTicketNumber(item.id)}
                        </span>
                        <span
                          className={`${classes.histStatus} ${
                            isClosed
                              ? classes.histStatusOk
                              : classes.histStatusOpen
                          }`}
                        >
                          {formatTicketStatus(item.status)}
                        </span>
                        {item.isGroup && (
                          <span
                            className={`${classes.histStatus} ${classes.histStatusOpen}`}
                          >
                            Grupo
                          </span>
                        )}
                      </div>
                      <div className={classes.histMeta}>
                        <AccessTimeIcon />
                        {new Date(
                          item.updatedAt || item.createdAt
                        ).toLocaleString("pt-BR")}
                      </div>
                      {item.queue?.name && (
                        <div className={classes.histMeta}>
                          <AccountTreeOutlinedIcon />
                          Fila:{" "}
                          <b className={classes.histMetaStrong}>
                            {item.queue.name}
                          </b>
                        </div>
                      )}
                      <div className={classes.histMeta}>
                        <PersonOutlineIcon />
                        {item.user?.name ? (
                          <>
                            Atendido por{" "}
                            <b className={classes.histMetaStrong}>
                              {item.user.name}
                            </b>
                          </>
                        ) : (
                          "Sem atendente"
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <Dialog
        open={documentDialogOpen}
        onClose={() => {
          if (documentSaving) {
            return;
          }
          setDocumentDialogOpen(false);
          setDocumentError("");
          setPendingAction(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>CPF/CNPJ obrigatório</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary">
            Esta ação requer CPF/CNPJ válido. O documento sera salvo no cadastro do contato antes da execução.
          </Typography>

          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label="Tipo de cadastro"
            value={documentForm.personType}
            onChange={event => {
              const nextValue = normalizePersonType(event.target.value);
              setDocumentForm(prev => ({
                ...prev,
                personType: nextValue
              }));
            }}
            disabled={documentSaving}
          >
            <MenuItem value="">Definir automaticamente</MenuItem>
            <MenuItem value="PF">PF</MenuItem>
            <MenuItem value="PJ">PJ</MenuItem>
          </TextField>

          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label="CPF/CNPJ"
            placeholder="000.000.000-00"
            value={documentForm.document}
            onChange={event => {
              const nextValue = formatDocument(event.target.value);
              setDocumentForm(prev => ({
                ...prev,
                document: nextValue
              }));
            }}
            disabled={documentSaving}
            error={Boolean(documentError)}
            helperText={documentError || "Informe CPF/CNPJ válido para continuar."}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDocumentDialogOpen(false);
              setDocumentError("");
              setPendingAction(null);
            }}
            disabled={documentSaving}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveDocumentAndExecute}
            color="primary"
            variant="contained"
            disabled={documentSaving}
          >
            {documentSaving ? "Salvando..." : "Salvar e executar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default ContactDrawer;
